<script setup lang="ts">
  import type { NuxtError } from '#app';

  const props = defineProps({
    error: Object as () => NuxtError,
  });

  const handleError = () => clearError({ redirect: '/' });

  const errorTitle = computed(() => {
    const code = props.error?.statusCode || 404;

    switch (code) {
      case 404:
        return 'Page Not Found';
      case 500:
        return 'Internal Server Error';
      default:
        return 'Error';
    }
  });

  const errorDescription = computed(() => {
    const code = props.error?.statusCode || 404;

    switch (code) {
      case 404:
        return 'The page you are looking for might have been removed or is temporarily unavailable';
      case 500:
        return 'An internal server error occurred.';
      default:
        return 'An error occurred.';
    }
  });
</script>

<template>
  <div class="bg-black w-[100dvw] h-[100dvh] px-5 relative">
    <!-- background image -->
    <NuxtImg
      src="/images/404/background.webp"
      alt="Error background"
      class="absolute inset-0 w-full h-full object-cover z-0"
      preload
      format="webp"
    />

    <!-- blue spots mobile -->
    <div class="lg:hidden absolute w-[100dvw] h-[100dvh] pointer-events-none z-0">
      <div
        class="pointer-events-none absolute w-[1px] h-[1px] shadow-[0_0_250px_250px_hsl(216deg_15%42%/_15%)] top-0 left-0"
      />
      <div
        class="pointer-events-none absolute w-[1px] h-[1px] shadow-[0_0_200px_200px_hsl(216deg_87%27%/_50%)] top-[3%] right-[-3%]"
      />
      <div
        class="pointer-events-none absolute w-[1px] h-[1px] bottom-[10%] right-[15%] shadow-[0_0_200px_200px_hsl(216deg_87%27%/_60%)]"
      />
    </div>

    <!-- blue spots desktop -->
    <div class="hidden lg:block absolute w-[100dvw] h-[100dvh] pointer-events-none">
      <div class="pointer-events-none absolute w-[1px] h-[1px]" />
    </div>

    <!-- content -->
    <div class="max-w-[960px] mx-auto flex flex-col items-center justify-center h-full">
      <h3
        class="relative z-1 text-center font-barlow text-[20px] md:text-[24px] font-bold leading-[100%] text-white mb-2.5 lg:mb-6"
      >
        Error {{ error?.statusCode || 404 }}
      </h3>
      <h2
        class="relative z-1 text-center font-barlow text-[60px] md:text-[80px] font-bold leading-[120%] text-white mb-10 lg:mb-6"
      >
        {{ errorTitle }}
      </h2>
      <p
        class="relative z-1 text-center font-barlow text-[24px] font-medium leading-[150%] text-white mb-10 lg:text-[32px] lg:mb-6"
      >
        {{ errorDescription }}
      </p>
      <UButton class="relative z-1 h-[52px] min-w-[153px] flex justify-center items-center gap-3" @click="handleError">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M0 10C0 7.34784 1.05357 4.8043 2.92893 2.92893C4.8043 1.05357 7.34784 0 10 0C11.3132 0 12.6136 0.258658 13.8268 0.761205C15.0401 1.26375 16.1425 2.00035 17.0711 2.92893C17.9997 3.85752 18.7362 4.95991 19.2388 6.17317C19.7413 7.38642 20 8.68678 20 10C20 12.6522 18.9464 15.1957 17.0711 17.0711C15.1957 18.9464 12.6522 20 10 20C8.68678 20 7.38642 19.7413 6.17317 19.2388C4.95991 18.7362 3.85752 17.9997 2.92893 17.0711C1.05357 15.1957 0 12.6522 0 10ZM16 9H8L11.5 5.5L10.08 4.08L4.16 10L10.08 15.92L11.5 14.5L8 11H16V9Z"
            fill="white"
          />
        </svg>
        <span class="text-center font-inter text-[16px] font-bold leading-[100%] text-white">Go Back</span>
      </UButton>
    </div>
  </div>
</template>
